import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  LogOut, UserPlus, Trash2, Loader2, ShieldCheck, GraduationCap, UserRound,
  ClipboardList, BookOpenText, Globe, Plus, ToggleLeft, ToggleRight,
  KeyRound, Flame, Megaphone, Eye,
} from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const EASE = [0.22, 1, 0.36, 1];

const inputCls =
  "w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-900 outline-none transition-colors duration-200 placeholder:text-slate-400 focus:border-sky-500 focus:ring-2 focus:ring-sky-100";

const fileCls = `${inputCls} file:mr-3 file:rounded-full file:border-0 file:bg-sky-100 file:px-4 file:py-1.5 file:text-xs file:font-bold file:text-sky-800`;

const Card = ({ title, icon: Icon, children, testid }) => (
  <section className="rounded-3xl border border-slate-200 bg-white p-7 shadow-sm" data-testid={testid}>
    <div className="flex items-center gap-3">
      <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-sky-100 text-sky-700">
        <Icon size={17} />
      </span>
      <h2 className="font-display text-lg font-bold text-slate-900">{title}</h2>
    </div>
    <div className="mt-5">{children}</div>
  </section>
);

function ChangePasswordCard() {
  const [form, setForm] = useState({ current: "", next: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: form.current, new_password: form.next });
      toast.success("Password updated — use it from your next sign-in");
      setForm({ current: "", next: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card title="Change Password" icon={KeyRound} testid="change-password-card">
      <form onSubmit={submit} className="space-y-3">
        <input
          type="password" required placeholder="Current password" value={form.current}
          onChange={(e) => setForm({ ...form, current: e.target.value })}
          className={inputCls} data-testid="change-current-password-input"
        />
        <input
          type="password" required minLength={6} placeholder="New password (min 6 chars)" value={form.next}
          onChange={(e) => setForm({ ...form, next: e.target.value })}
          className={inputCls} data-testid="change-new-password-input"
        />
        <button
          type="submit" disabled={busy} data-testid="change-password-button"
          className="flex w-full items-center justify-center gap-2 rounded-full bg-sky-700 py-3 text-sm font-bold text-white transition-colors duration-200 hover:bg-sky-800 disabled:opacity-60"
        >
          {busy ? <Loader2 size={15} className="animate-spin" /> : <KeyRound size={15} />} Update password
        </button>
      </form>
    </Card>
  );
}

function AdminWorkspace() {
  const [users, setUsers] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [socials, setSocials] = useState([]);
  const [stories, setStories] = useState([]);
  const [notices, setNotices] = useState([]);
  const [twoFA, setTwoFA] = useState(true);
  const [waNumber, setWaNumber] = useState("");
  const [uForm, setUForm] = useState({ username: "", name: "", password: "", role: "student" });
  const [gForm, setGForm] = useState({ title: "", category: "General", summary: "", details: "" });
  const [sForm, setSForm] = useState({ platform: "", url: "", badge: "", handle: "" });
  const [stForm, setStForm] = useState({ title: "", author: "", body: "", kind: "text" });
  const [stFile, setStFile] = useState(null);
  const [nForm, setNForm] = useState({ title: "", note: "", kind: "pdf", gdoc_url: "" });
  const [nFile, setNFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [u, g, s, st, st2, n] = await Promise.all([
        api.get("/admin/users"),
        api.get("/gallery"),
        api.get("/socials"),
        api.get("/admin/settings"),
        api.get("/stories"),
        api.get("/notices"),
      ]);
      setUsers(u.data);
      setGallery(g.data);
      setSocials(s.data);
      setTwoFA(st.data.admin_2fa_enabled);
      setWaNumber(st.data.admin_2fa_whatsapp || "");
      setStories(st2.data);
      setNotices(n.data);
    } catch {
      toast.error("Could not load workspace data");
    }
  };

  useEffect(() => { load(); }, []);

  const createUser = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/admin/users", uForm);
      toast.success(`${data.role} created — ${data.username} / ${uForm.password}`);
      setUForm({ username: "", name: "", password: "", role: "student" });
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const deleteUser = async (id, username) => {
    try {
      await api.delete(`/admin/users/${id}`);
      toast.success(`Removed ${username}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const addGallery = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/admin/gallery", gForm);
      toast.success("Information card published");
      setGForm({ title: "", category: "General", summary: "", details: "" });
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const addSocial = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/admin/socials", sForm);
      toast.success("Social link added");
      setSForm({ platform: "", url: "", badge: "", handle: "" });
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const addStory = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("title", stForm.title);
      fd.append("author", stForm.author);
      fd.append("kind", stForm.kind);
      if (stForm.kind === "pdf") {
        if (!stFile) {
          toast.error("Attach a PDF file");
          setBusy(false);
          return;
        }
        fd.append("file", stFile);
      } else {
        fd.append("body", stForm.body);
      }
      await api.post("/admin/stories", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Story published to Inspirations");
      setStForm({ title: "", author: "", body: "", kind: "text" });
      setStFile(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const addNotice = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("title", nForm.title);
      fd.append("note", nForm.note);
      fd.append("kind", nForm.kind);
      if (nForm.kind === "gdoc") {
        fd.append("gdoc_url", nForm.gdoc_url);
      } else {
        if (!nFile) {
          toast.error("Attach a PDF file");
          setBusy(false);
          return;
        }
        fd.append("file", nFile);
      }
      await api.post("/admin/notices", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Notice published");
      setNForm({ title: "", note: "", kind: "pdf", gdoc_url: "" });
      setNFile(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const toggle2FA = async () => {
    try {
      const { data } = await api.post("/admin/settings/2fa", { enabled: !twoFA });
      setTwoFA(data.admin_2fa_enabled);
      toast.success(`Admin 2FA ${data.admin_2fa_enabled ? "enabled" : "disabled"}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const saveWa = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/admin/settings/whatsapp", { number: waNumber });
      setWaNumber(data.admin_2fa_whatsapp);
      toast.success("2FA WhatsApp number updated");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <div className="grid gap-6 lg:grid-cols-2" data-testid="admin-workspace">
      <Card title="Create Mentor / Student" icon={UserPlus} testid="admin-create-user-card">
        <form onSubmit={createUser} className="space-y-3">
          <input required minLength={3} placeholder="Username" value={uForm.username}
            onChange={(e) => setUForm({ ...uForm, username: e.target.value })}
            className={inputCls} data-testid="admin-username-input" />
          <input placeholder="Full name (optional)" value={uForm.name}
            onChange={(e) => setUForm({ ...uForm, name: e.target.value })}
            className={inputCls} data-testid="admin-name-input" />
          <input required minLength={6} placeholder="Password (min 6 chars)" value={uForm.password}
            onChange={(e) => setUForm({ ...uForm, password: e.target.value })}
            className={inputCls} data-testid="admin-password-input" />
          <select value={uForm.role} onChange={(e) => setUForm({ ...uForm, role: e.target.value })}
            className={inputCls} data-testid="admin-role-select">
            <option value="student">Student</option>
            <option value="mentor">Mentor</option>
          </select>
          <button type="submit" disabled={busy} data-testid="admin-create-user-button"
            className="flex w-full items-center justify-center gap-2 rounded-full bg-sky-700 py-3 text-sm font-bold text-white transition-colors duration-200 hover:bg-sky-800 disabled:opacity-60">
            {busy ? <Loader2 size={15} className="animate-spin" /> : <UserPlus size={15} />} Create credentials
          </button>
        </form>
      </Card>

      <Card title="Issued Accounts" icon={ShieldCheck} testid="admin-users-list-card">
        {users.length === 0 ? (
          <p className="text-sm text-slate-500" data-testid="admin-users-empty">No mentor or student accounts yet.</p>
        ) : (
          <ul className="divide-y divide-slate-100">
            {users.map((u) => (
              <li key={u.id} className="flex items-center justify-between py-3" data-testid={`admin-user-row-${u.username}`}>
                <div className="flex items-center gap-3">
                  <span className="flex h-8 w-8 items-center justify-center rounded-full bg-slate-100 text-slate-500">
                    {u.role === "mentor" ? <UserRound size={14} /> : <GraduationCap size={14} />}
                  </span>
                  <div>
                    <p className="text-sm font-semibold text-slate-800">{u.name || u.username}</p>
                    <p className="text-xs text-slate-500">@{u.username} · {u.role}</p>
                  </div>
                </div>
                <button onClick={() => deleteUser(u.id, u.username)} data-testid={`admin-user-delete-${u.username}`}
                  className="rounded-full p-2 text-slate-400 transition-colors duration-200 hover:bg-red-50 hover:text-red-600">
                  <Trash2 size={15} />
                </button>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Card title="Inspiration Stories" icon={Flame} testid="admin-stories-card">
        <form onSubmit={addStory} className="space-y-3">
          <input required placeholder="Story title" value={stForm.title}
            onChange={(e) => setStForm({ ...stForm, title: e.target.value })}
            className={inputCls} data-testid="admin-story-title-input" />
          <select value={stForm.kind} onChange={(e) => setStForm({ ...stForm, kind: e.target.value })}
            className={inputCls} data-testid="admin-story-kind-select">
            <option value="text">Write story</option>
            <option value="pdf">Upload PDF</option>
          </select>
          <input placeholder="Author (optional)" value={stForm.author}
            onChange={(e) => setStForm({ ...stForm, author: e.target.value })}
            className={inputCls} data-testid="admin-story-author-input" />
          {stForm.kind === "pdf" ? (
            <input type="file" accept="application/pdf"
              onChange={(e) => setStFile(e.target.files?.[0] || null)}
              className={fileCls}
              data-testid="admin-story-file-input" />
          ) : (
            <textarea required placeholder="The story…" rows={4} value={stForm.body}
              onChange={(e) => setStForm({ ...stForm, body: e.target.value })}
              className={inputCls} data-testid="admin-story-body-input" />
          )}
          <button type="submit" disabled={busy} data-testid="admin-story-add-button"
            className="flex w-full items-center justify-center gap-2 rounded-full bg-sky-700 py-3 text-sm font-bold text-white transition-colors duration-200 hover:bg-sky-800 disabled:opacity-60">
            {busy ? <Loader2 size={15} className="animate-spin" /> : <Plus size={15} />} Publish story
          </button>
        </form>
        {stories.length > 0 && (
          <ul className="mt-4 divide-y divide-slate-100 border-t border-slate-100">
            {stories.map((s) => (
              <li key={s.id} className="flex items-center justify-between py-2.5" data-testid={`admin-story-row-${s.id}`}>
                <span className="truncate pr-3 text-sm text-slate-700">
                  {s.title}
                  <span className="ml-2 rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-slate-500">
                    {s.kind === "pdf" ? "PDF" : "Text"}
                  </span>
                </span>
                <button onClick={async () => { await api.delete(`/admin/stories/${s.id}`).catch(() => {}); load(); }}
                  data-testid={`admin-story-delete-${s.id}`}
                  className="rounded-full p-2 text-slate-400 transition-colors duration-200 hover:bg-red-50 hover:text-red-600">
                  <Trash2 size={14} />
                </button>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Card title="Notices" icon={Megaphone} testid="admin-notices-card">
        <form onSubmit={addNotice} className="space-y-3">
          <input required placeholder="Notice title" value={nForm.title}
            onChange={(e) => setNForm({ ...nForm, title: e.target.value })}
            className={inputCls} data-testid="admin-notice-title-input" />
          <input placeholder="Short note (optional)" value={nForm.note}
            onChange={(e) => setNForm({ ...nForm, note: e.target.value })}
            className={inputCls} data-testid="admin-notice-note-input" />
          <select value={nForm.kind} onChange={(e) => setNForm({ ...nForm, kind: e.target.value })}
            className={inputCls} data-testid="admin-notice-kind-select">
            <option value="pdf">Upload PDF</option>
            <option value="gdoc">Google Doc link</option>
          </select>
          {nForm.kind === "pdf" ? (
            <input type="file" accept="application/pdf"
              onChange={(e) => setNFile(e.target.files?.[0] || null)}
              className={fileCls}
              data-testid="admin-notice-file-input" />
          ) : (
            <input required type="url" placeholder="https://docs.google.com/…" value={nForm.gdoc_url}
              onChange={(e) => setNForm({ ...nForm, gdoc_url: e.target.value })}
              className={inputCls} data-testid="admin-notice-gdoc-input" />
          )}
          <button type="submit" disabled={busy} data-testid="admin-notice-add-button"
            className="flex w-full items-center justify-center gap-2 rounded-full bg-sky-700 py-3 text-sm font-bold text-white transition-colors duration-200 hover:bg-sky-800 disabled:opacity-60">
            {busy ? <Loader2 size={15} className="animate-spin" /> : <Plus size={15} />} Publish notice
          </button>
        </form>
        {notices.length > 0 && (
          <ul className="mt-4 divide-y divide-slate-100 border-t border-slate-100">
            {notices.map((n) => (
              <li key={n.id} className="flex items-center justify-between py-2.5" data-testid={`admin-notice-row-${n.id}`}>
                <span className="truncate pr-3 text-sm text-slate-700">
                  {n.title}
                  <span className="ml-2 rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-slate-500">
                    {n.kind === "pdf" ? "PDF" : "Doc"}
                  </span>
                </span>
                <button onClick={async () => { await api.delete(`/admin/notices/${n.id}`).catch(() => {}); load(); }}
                  data-testid={`admin-notice-delete-${n.id}`}
                  className="rounded-full p-2 text-slate-400 transition-colors duration-200 hover:bg-red-50 hover:text-red-600">
                  <Trash2 size={14} />
                </button>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Card title="Information Gallery" icon={BookOpenText} testid="admin-gallery-card">
        <form onSubmit={addGallery} className="space-y-3">
          <input required placeholder="Title" value={gForm.title}
            onChange={(e) => setGForm({ ...gForm, title: e.target.value })}
            className={inputCls} data-testid="admin-gallery-title-input" />
          <input placeholder="Category" value={gForm.category}
            onChange={(e) => setGForm({ ...gForm, category: e.target.value })}
            className={inputCls} data-testid="admin-gallery-category-input" />
          <textarea placeholder="Short summary" rows={2} value={gForm.summary}
            onChange={(e) => setGForm({ ...gForm, summary: e.target.value })}
            className={inputCls} data-testid="admin-gallery-summary-input" />
          <textarea placeholder="Full details" rows={3} value={gForm.details}
            onChange={(e) => setGForm({ ...gForm, details: e.target.value })}
            className={inputCls} data-testid="admin-gallery-details-input" />
          <button type="submit" disabled={busy} data-testid="admin-gallery-add-button"
            className="flex w-full items-center justify-center gap-2 rounded-full bg-sky-700 py-3 text-sm font-bold text-white transition-colors duration-200 hover:bg-sky-800 disabled:opacity-60">
            <Plus size={15} /> Publish card
          </button>
        </form>
        {gallery.length > 0 && (
          <ul className="mt-4 divide-y divide-slate-100 border-t border-slate-100">
            {gallery.map((g) => (
              <li key={g.id} className="flex items-center justify-between py-2.5" data-testid={`admin-gallery-row-${g.id}`}>
                <span className="truncate pr-3 text-sm text-slate-700">{g.title}</span>
                <button onClick={async () => { await api.delete(`/admin/gallery/${g.id}`).catch(() => {}); load(); }}
                  data-testid={`admin-gallery-delete-${g.id}`}
                  className="rounded-full p-2 text-slate-400 transition-colors duration-200 hover:bg-red-50 hover:text-red-600">
                  <Trash2 size={14} />
                </button>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Card title="Social Links" icon={Globe} testid="admin-socials-card">
        <form onSubmit={addSocial} className="space-y-3">
          <input required placeholder="Platform (e.g. YouTube)" value={sForm.platform}
            onChange={(e) => setSForm({ ...sForm, platform: e.target.value })}
            className={inputCls} data-testid="admin-social-platform-input" />
          <input required type="url" placeholder="https://…" value={sForm.url}
            onChange={(e) => setSForm({ ...sForm, url: e.target.value })}
            className={inputCls} data-testid="admin-social-url-input" />
          <input placeholder="Badge (optional, e.g. Live Classes)" value={sForm.badge}
            onChange={(e) => setSForm({ ...sForm, badge: e.target.value })}
            className={inputCls} data-testid="admin-social-badge-input" />
          <input placeholder="Username only (optional, e.g. @jyotiacademy) — shown publicly instead of the link" value={sForm.handle}
            onChange={(e) => setSForm({ ...sForm, handle: e.target.value })}
            className={inputCls} data-testid="admin-social-handle-input" />
          <button type="submit" disabled={busy} data-testid="admin-social-add-button"
            className="flex w-full items-center justify-center gap-2 rounded-full bg-sky-700 py-3 text-sm font-bold text-white transition-colors duration-200 hover:bg-sky-800 disabled:opacity-60">
            <Plus size={15} /> Add social link
          </button>
        </form>
        {socials.length > 0 && (
          <ul className="mt-4 divide-y divide-slate-100 border-t border-slate-100">
            {socials.map((s) => (
              <li key={s.id} className="flex items-center justify-between py-2.5" data-testid={`admin-social-row-${s.id}`}>
                <span className="truncate pr-3 text-sm text-slate-700">{s.platform}</span>
                <button onClick={async () => { await api.delete(`/admin/socials/${s.id}`).catch(() => {}); load(); }}
                  data-testid={`admin-social-delete-${s.id}`}
                  className="rounded-full p-2 text-slate-400 transition-colors duration-200 hover:bg-red-50 hover:text-red-600">
                  <Trash2 size={14} />
                </button>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Card title="Security" icon={ShieldCheck} testid="admin-security-card">
        <button onClick={toggle2FA} data-testid="admin-2fa-toggle"
          className="flex w-full items-center justify-between rounded-2xl border border-slate-200 px-5 py-4 transition-colors duration-200 hover:border-sky-300">
          <span className="text-left">
            <span className="block text-sm font-bold text-slate-800">Admin WhatsApp 2FA</span>
            <span className="mt-0.5 block text-xs text-slate-500">
              {twoFA ? "On — verification code required at admin sign-in" : "Off — admin signs in with password only"}
            </span>
          </span>
          {twoFA ? <ToggleRight size={30} className="text-sky-700" /> : <ToggleLeft size={30} className="text-slate-400" />}
        </button>
        <form onSubmit={saveWa} className="mt-4 space-y-3">
          <label className="block">
            <span className="mb-1.5 block text-xs font-semibold uppercase tracking-widest text-slate-500">
              2FA WhatsApp number
            </span>
            <input
              value={waNumber}
              onChange={(e) => setWaNumber(e.target.value)}
              placeholder="e.g. 8455079198"
              className={inputCls}
              data-testid="admin-whatsapp-input"
            />
          </label>
          <button type="submit" data-testid="admin-whatsapp-save-button"
            className="flex w-full items-center justify-center gap-2 rounded-full border-2 border-sky-700 py-2.5 text-sm font-bold text-sky-700 transition-colors duration-200 hover:bg-sky-700 hover:text-white">
            Save number
          </button>
        </form>
      </Card>

      <ChangePasswordCard />
    </div>
  );
}

function VisitorStats() {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    api.get("/visits/stats").then((r) => setStats(r.data)).catch(() => setStats(false));
  }, []);
  if (!stats) return null;
  return (
    <div className="grid gap-4 sm:grid-cols-2" data-testid="visitor-stats">
      <div className="flex items-center gap-4 rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
        <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-sky-700 text-white">
          <Eye size={19} />
        </span>
        <div>
          <p className="font-display text-2xl font-bold text-slate-900" data-testid="visitor-total">{stats.total}</p>
          <p className="text-xs font-semibold uppercase tracking-widest text-slate-500">Total site visits</p>
        </div>
      </div>
      <div className="flex items-center gap-4 rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
        <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-amber-500 text-white">
          <Eye size={19} />
        </span>
        <div>
          <p className="font-display text-2xl font-bold text-slate-900" data-testid="visitor-today">{stats.today}</p>
          <p className="text-xs font-semibold uppercase tracking-widest text-slate-500">Visits today</p>
        </div>
      </div>
    </div>
  );
}

function MemberWelcome({ user }) {
  const links = [
    { label: "Enrollment Form (OTET 1 & 2)", href: "https://tally.so/r/J9yWGo", icon: ClipboardList, testid: "member-tally-link" },
    { label: "Inspirations", to: "/inspirations", icon: Flame, testid: "member-inspirations-link" },
    { label: "Notice Board", to: "/notices", icon: Megaphone, testid: "member-notices-link" },
    { label: "Information Gallery", to: "/gallery", icon: BookOpenText, testid: "member-gallery-link" },
    { label: "Socials Hub", to: "/socials", icon: Globe, testid: "member-socials-link" },
  ];
  return (
    <div className="grid gap-5 sm:grid-cols-2" data-testid="member-welcome">
      {user.role === "mentor" && (
        <div className="sm:col-span-2">
          <VisitorStats />
        </div>
      )}
      {links.map((l, i) => {
        const Icon = l.icon;
        const inner = (
          <>
            <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-sky-700 text-white">
              <Icon size={19} />
            </span>
            <span className="mt-5 block font-display text-lg font-bold text-slate-900">{l.label}</span>
            <span className="mt-1 block text-sm text-slate-500">Tap to open</span>
          </>
        );
        const cls = "block rounded-3xl border border-slate-200 bg-white p-7 shadow-sm transition-colors duration-200 hover:border-sky-300 hover:bg-sky-50/50";
        return (
          <motion.div key={l.label} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.08, duration: 0.7, ease: EASE }}>
            {l.href ? (
              <a href={l.href} target="_blank" rel="noopener noreferrer" className={cls} data-testid={l.testid}>{inner}</a>
            ) : (
              <Link to={l.to} className={cls} data-testid={l.testid}>{inner}</Link>
            )}
          </motion.div>
        );
      })}
      <div className="sm:col-span-2">
        <ChangePasswordCard />
      </div>
    </div>
  );
}

export default function PortalPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user === false) navigate("/");
  }, [user, navigate]);

  if (!user) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50" data-testid="portal-loading">
        <Loader2 size={22} className="animate-spin text-sky-700" />
      </div>
    );
  }

  return (
    <div data-testid="portal-page" className="min-h-screen bg-slate-50">
      <Navbar />
      <main className="mx-auto max-w-6xl px-6 pb-28 pt-32 sm:px-10">
        <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: EASE }}
          className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.3em] text-sky-700">
              {user.role} portal
            </p>
            <h1 className="mt-3 font-display text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl" data-testid="portal-greeting">
              Namaskar, {user.name || user.username}.
            </h1>
          </div>
          <button
            onClick={async () => { await logout(); navigate("/"); toast.success("Signed out"); }}
            data-testid="portal-logout-button"
            className="flex items-center gap-2 rounded-full border border-slate-300 px-5 py-2.5 text-sm font-bold text-slate-600 transition-colors duration-200 hover:border-red-300 hover:bg-red-50 hover:text-red-600"
          >
            <LogOut size={15} /> Sign out
          </button>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15, duration: 0.8, ease: EASE }} className="mt-12">
          {user.role === "admin" ? <AdminWorkspace /> : <MemberWelcome user={user} />}
        </motion.div>
      </main>
    </div>
  );
}
